/**
 * LandingPage.tsx — MER Algo Footprints public landing.
 *
 * Aesthetic: institutional dark — precision instruments for serious traders.
 * Dark navy foundation, gold accents, monospace data feel.
 * Clean, minimal, no noise. One clear CTA.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const FEATURES = [
  { icon: '⬡', label: 'Gamma Engine v2',   desc: 'Chain-state GEX with ThetaData — walls, flip zones, concentration' },
  { icon: '◈', label: 'Trap Intelligence',  desc: 'Structural + gamma + liquidity trap detection with regimeConfidence' },
  { icon: '⊞', label: 'Volatility Context', desc: 'VIX-aware regime modifiers across thesis, trap, and breakout signals' },
  { icon: '▣', label: 'AI Thesis Engine',   desc: 'Deterministic evidence layer feeding a structured AI decision loop' },
];

export function LandingPage() {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 60); return () => clearTimeout(t); }, []);

  return (
    <div className="mer-full-page" style={{
      background: '#080F1C',
      color: '#E8EDF5',
      fontFamily: "'Courier New', Courier, monospace",
      overflowX: 'hidden',
    }}>
      {/* ── Subtle grid background ── */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        backgroundImage: `linear-gradient(rgba(26,48,80,0.18) 1px, transparent 1px),
                          linear-gradient(90deg, rgba(26,48,80,0.18) 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
      }} />

      {/* ── Nav ── */}
      <nav style={{
        position: 'relative', zIndex: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 48px',
        borderBottom: '1px solid rgba(26,48,80,0.6)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg viewBox="0 0 32 36" width="28" height="32">
            <path d="M2 2 L30 2 L30 24 Q30 34 16 36 Q2 34 2 24 Z" fill="#0D1F35" stroke="#C9A84C" strokeWidth="1.2"/>
            <polyline points="6,28 11,20 16,14 21,17 27,22" fill="none" stroke="#C9A84C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: 3, color: '#C9A84C' }}>MER</span>
          <span style={{ fontSize: 9, letterSpacing: 1.5, color: '#4A6FA5', marginTop: 1 }}>ALGO FOOTPRINTS</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/login" style={{
            padding: '7px 18px', fontSize: 11, letterSpacing: 1,
            color: '#9BAFC8', textDecoration: 'none',
            border: '1px solid #1A3050', borderRadius: 3,
            transition: 'all 0.15s',
          }}>
            SIGN IN
          </Link>
          <Link to="/signup" style={{
            padding: '7px 18px', fontSize: 11, letterSpacing: 1,
            color: '#0A1628', background: '#C9A84C', textDecoration: 'none',
            border: '1px solid #C9A84C', borderRadius: 3, fontWeight: 700,
          }}>
            ACCESS TO MER
          </Link>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section style={{
        position: 'relative', zIndex: 1,
        padding: '100px 48px 80px',
        maxWidth: 860, margin: '0 auto',
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : 'translateY(16px)',
        transition: 'opacity 0.5s, transform 0.5s',
      }}>
        <div style={{
          fontSize: 10, letterSpacing: 3, color: '#C9A84C',
          marginBottom: 24, fontWeight: 700,
        }}>
          MALDONADO EQUITY RESEARCH · INTRADAY INTELLIGENCE PLATFORM
        </div>

        <h1 style={{
          fontSize: 'clamp(36px, 5vw, 58px)',
          fontWeight: 900,
          lineHeight: 1.1,
          letterSpacing: -1,
          margin: '0 0 24px',
          fontFamily: 'Georgia, "Times New Roman", serif',
          color: '#F0F4FA',
        }}>
          Where gamma structure<br />
          <span style={{ color: '#C9A84C' }}>meets machine precision.</span>
        </h1>

        <p style={{
          fontSize: 16, lineHeight: 1.7, color: '#8BACC8',
          maxWidth: 560, margin: '0 0 40px',
        }}>
          AI-assisted intraday decision engine combining Gamma Engine v2,
          structural market analysis, and deterministic trap intelligence
          into a single auditable signal chain.
        </p>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Link to="/signup" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '13px 28px', fontSize: 12, letterSpacing: 1.5,
            fontWeight: 700, textDecoration: 'none',
            background: '#C9A84C', color: '#080F1C', borderRadius: 3,
            border: '1px solid #C9A84C',
          }}>
            ACCESS TO MER →
          </Link>
          <Link to="/login" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '13px 28px', fontSize: 12, letterSpacing: 1.5,
            textDecoration: 'none', color: '#9BAFC8',
            border: '1px solid #1A3050', borderRadius: 3,
          }}>
            SIGN IN
          </Link>
        </div>
      </section>

      {/* ── Feature grid ── */}
      <section style={{
        position: 'relative', zIndex: 1,
        padding: '20px 48px 100px',
        maxWidth: 900, margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 1,
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.7s 0.2s',
      }}>
        {FEATURES.map(f => (
          <div key={f.label} style={{
            padding: '28px 24px',
            background: 'rgba(13,31,53,0.5)',
            border: '1px solid rgba(26,48,80,0.7)',
          }}>
            <div style={{ fontSize: 20, marginBottom: 10, color: '#C9A84C' }}>{f.icon}</div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, marginBottom: 8, color: '#E8EDF5' }}>
              {f.label.toUpperCase()}
            </div>
            <div style={{ fontSize: 11, lineHeight: 1.6, color: '#5A7FA8' }}>{f.desc}</div>
          </div>
        ))}
      </section>

      {/* ── Footer ── */}
      <footer style={{
        position: 'relative', zIndex: 1,
        borderTop: '1px solid rgba(26,48,80,0.5)',
        padding: '16px 48px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontSize: 9, letterSpacing: 1, color: '#2A4060',
      }}>
        <span>MALDONADO EQUITY RESEARCH © 2026</span>
        <span>FOR INFORMATIONAL PURPOSES ONLY · NOT INVESTMENT ADVICE</span>
      </footer>
    </div>
  );
}
